import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, ExternalLink, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MapSection() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [content, setContent] = useState({
    hero_title: 'Find Us',
    hero_subtitle: 'Visit our premium coworking spaces across Bangalore',
    hero_image_url: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locationsRes, contentRes] = await Promise.all([
          axios.get(`${API}/locations`),
          axios.get(`${API}/find-us-content`)
        ]);
        setLocations(locationsRes.data);
        setContent(contentRes.data);
        if (locationsRes.data.length > 0) {
          setSelectedLocation(locationsRes.data[0]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleLocationChange = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      setSelectedLocation(location);
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white" data-testid="map-section">
      <div className="container-wide mx-auto px-4">
        {/* Optional Hero Image */}
        {content.hero_image_url && (
          <div className="mb-8 -mt-12 sm:-mt-16 md:-mt-20 lg:-mt-24">
            <div 
              className="h-[200px] sm:h-[250px] md:h-[300px] bg-cover bg-center rounded-lg relative"
              style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${content.hero_image_url})`
              }}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2">{content.hero_title}</h2>
                {content.hero_subtitle && (
                  <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl">{content.hero_subtitle}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Heading with red underline (shown when no image) */}
        {!content.hero_image_url && (
          <div className="mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#222] mb-2">{content.hero_title}</h2>
            {content.hero_subtitle && (
              <p className="text-lg text-gray-600 mb-2">{content.hero_subtitle}</p>
            )}
            <div className="w-24 h-1 bg-[#E12429]"></div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map View - 2/3 width */}
          <div className="lg:col-span-2">
            <div className="relative h-[600px] bg-gray-100 rounded-lg overflow-hidden shadow-lg">
              {/* Interactive Map */}
              <div className="absolute inset-0">
                <iframe
                  src="https://maps.google.com/maps?q=Sarjapur+Bangalore,Hebbal+Bangalore&output=embed&z=11"
                  className="w-full h-full"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="The Circuit Locations"
                ></iframe>
              </div>

              {/* Location Dropdown - Top Right */}
              <div className="absolute top-4 right-4 z-10">
                <div className="bg-white rounded-lg shadow-xl p-2 min-w-[240px]">
                  <Select value={selectedLocation?.id} onValueChange={handleLocationChange}>
                    <SelectTrigger className="border-none shadow-none focus:ring-0 font-semibold text-base" data-testid="location-dropdown">
                      <div className="flex items-center space-x-2">
                        <MapPin className="text-[#E12429]" size={20} />
                        <SelectValue placeholder="Select Location" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          <div className="flex items-center space-x-2">
                            <MapPin className="text-[#E12429]" size={16} />
                            <span>{location.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Visual Location Markers on Map */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Sarjapur Marker - Bottom Right Position */}
                <div className="absolute" style={{ bottom: '35%', right: '30%' }}>
                  <button
                    onClick={() => setSelectedLocation(locations[0])}
                    className="pointer-events-auto group cursor-pointer"
                    data-testid="map-pin-sarjapur"
                  >
                    <div className="relative flex flex-col items-center">
                      {/* Pin Icon */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transform transition-all group-hover:scale-110 ${
                        selectedLocation?.area === 'Sarjapur' ? 'bg-[#E12429] animate-bounce' : 'bg-white'
                      }`}>
                        <MapPin className={selectedLocation?.area === 'Sarjapur' ? 'text-white' : 'text-[#E12429]'} size={24} />
                      </div>
                      {/* Location Label */}
                      <div className={`mt-2 px-3 py-1 rounded-full text-xs font-bold shadow-md ${
                        selectedLocation?.area === 'Sarjapur' ? 'bg-[#E12429] text-white' : 'bg-white text-[#222]'
                      }`}>
                        Sarjapur
                      </div>
                    </div>
                  </button>
                </div>

                {/* Hebbal Marker - Top Position */}
                <div className="absolute" style={{ top: '25%', left: '45%' }}>
                  <button
                    onClick={() => setSelectedLocation(locations[1])}
                    className="pointer-events-auto group cursor-pointer"
                    data-testid="map-pin-hebbal"
                  >
                    <div className="relative flex flex-col items-center">
                      {/* Pin Icon */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transform transition-all group-hover:scale-110 ${
                        selectedLocation?.area === 'Hebbal' ? 'bg-[#E12429] animate-bounce' : 'bg-white'
                      }`}>
                        <MapPin className={selectedLocation?.area === 'Hebbal' ? 'text-white' : 'text-[#E12429]'} size={24} />
                      </div>
                      {/* Location Label */}
                      <div className={`mt-2 px-3 py-1 rounded-full text-xs font-bold shadow-md ${
                        selectedLocation?.area === 'Hebbal' ? 'bg-[#E12429] text-white' : 'bg-white text-[#222]'
                      }`}>
                        Hebbal
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Location Details Card - 1/3 width */}
          <div className="lg:col-span-1">
            {selectedLocation && (
              <div
                className="bg-white rounded-lg border-2 border-gray-200 p-6 shadow-lg h-[600px] overflow-y-auto"
                data-testid="location-details-card"
              >
                {/* Location Image */}
                <div className="relative h-48 rounded-lg overflow-hidden mb-6">
                  <img
                    src={selectedLocation.image}
                    alt={selectedLocation.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                      Open Now
                    </span>
                  </div>
                </div>

                {/* Location Name */}
                <h3 className="text-2xl font-bold text-[#222] mb-4">
                  {selectedLocation.name}
                </h3>

                {/* Location Details */}
                <div className="space-y-4 mb-6">
                  {/* Address */}
                  <div className="flex items-start space-x-3">
                    <MapPin className="text-[#E12429] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-[#222]">Address</p>
                      <p className="text-sm text-[#666]">{selectedLocation.address}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-3">
                    <Phone className="text-[#E12429] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-[#222]">Phone</p>
                      <p className="text-sm text-[#666]">+91 98765 43210</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-3">
                    <Mail className="text-[#E12429] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-[#222]">Email</p>
                      <p className="text-sm text-[#666]">info@thecircuit.work</p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start space-x-3">
                    <Clock className="text-[#E12429] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-[#222]">Hours</p>
                      <p className="text-sm text-[#666]">Mon - Sat: 9:00 AM - 7:00 PM</p>
                      <p className="text-sm text-[#666]">Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-[#222] mb-2">Features</p>
                  <div className="space-y-2">
                    <p className="text-sm text-[#666]">
                      {selectedLocation.features.join(', ')}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <a
                    href={selectedLocation.map_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full btn-primary flex items-center justify-center gap-2 text-center"
                  >
                    <ExternalLink size={20} />
                    <span className="font-semibold">Get Directions</span>
                  </a>
                  <button className="w-full btn-secondary">
                    Schedule a Visit
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}